import React, { useEffect, useState, useCallback, useRef } from 'react';
import { showToast } from '../common/Toaster';
import { Plus, Trash2, Check, Lock, ArrowRight, ArrowLeft, Clock, Info, Pencil, AlertTriangle, ShieldCheck, Send, MessageSquare } from 'lucide-react';
import planApi, { PlanRole } from '../../services/planApi';
import FodaExampleModal from './FodaExampleModal';
import './foda-wizard.scss';

const AREAS = ['LIDERAZGO', 'COMUNICACIÓN', 'ESTRATEGIA', 'EQUIPOS', 'TÉCNICO', 'NETWORKING', 'VISIBILIDAD', 'BIENESTAR', 'INTERCULTURAL'];

interface Props {
  relId: number;
  role: PlanRole;
  counterpart?: string;
  onChanged: () => void;
}

interface Quad {
  key: string;
  label: string;
  color: string;
  bg: string;
  desc: string;
  questions: string[];
  consejo: string;
  ejemplos: string[];
  add: string;
  hint: string;
}

const QUADRANTS: Quad[] = [
  {
    key: 'fortaleza', label: 'Fortalezas', color: '#1F6F4A', bg: '#EAF1EC',
    desc: 'Identifica lo que ya constituye una ventaja diferencial en tu rol o trayectoria reciente.',
    questions: [
      '¿Cuáles son mis competencias más sólidas según las evaluaciones?',
      '¿Qué rasgos de mi perfil generan confianza en otros?',
      '¿Dónde tengo mayor agilidad de aprendizaje?',
    ],
    consejo: 'Una capacidad nombrada con un caso que la respalde. La generalidad no le sirve a tu mentor.',
    ejemplos: ['Liderazgo en M&A', 'Visión financiera estratégica', 'Gestión de equipos senior'],
    add: 'Agregar fortaleza', hint: 'Capacidad o caso con evidencia concreta',
  },
  {
    key: 'debilidad', label: 'Debilidades', color: '#C8102E', bg: '#FBECEE',
    desc: 'Las brechas nombradas con precisión son las que tu mentor puede ayudar a cerrar. No es autocrítica: es información estratégica.',
    questions: [
      '¿Qué competencias muestran mayor brecha en el perfil de liderazgo?',
      '¿Qué dimensiones de agilidad de aprendizaje necesito fortalecer?',
      '¿Qué factores de riesgo se activan bajo presión con más frecuencia?',
    ],
    consejo: 'Lo que se repite en el feedback suele ser real. Edítate menos: tu mentor trabaja con la versión sin pulir.',
    ejemplos: ['Comunicación con audiencias no técnicas', 'Delegación efectiva', 'Visibilidad ante board global'],
    add: 'Agregar debilidad', hint: 'Capacidad o caso con evidencia concreta',
  },
  {
    key: 'oportunidad', label: 'Oportunidades', color: '#0F4E8A', bg: '#E9EFF6',
    desc: 'Movimientos en tu organización, industria o red que pueden activar tu trayectoria en los próximos 12-18 meses.',
    questions: [
      '¿Qué competencias en desarrollo pueden acelerarse con el programa?',
      '¿Qué motivadores puede activar el mentor?',
      '¿Qué brecha de aprendizaje tiene alto potencial si la trabajo?',
    ],
    consejo: 'Piensa en ventanas concretas de los próximos 12-18 meses, no en deseos generales.',
    ejemplos: ['Ventana de sucesión en un rol clave', 'Proyecto de alta visibilidad', 'Nueva red de stakeholders'],
    add: 'Agregar oportunidad', hint: 'Movimiento concreto y su horizonte',
  },
  {
    key: 'amenaza', label: 'Amenazas', color: '#8C5008', bg: '#F5EEE2',
    desc: 'Factores externos a vigilar. Realismo táctico, no anticipación pesimista.',
    questions: [
      '¿Qué factores de riesgo no gestionados pueden afectar mi reputación?',
      '¿Qué patrones de liderazgo en contextos de estrés generan fricción?',
      '¿Qué desconexión entre motivadores y contexto puede drenarme?',
    ],
    consejo: 'Nombra lo que puedes vigilar y mitigar, no lo que solo genera ansiedad.',
    ejemplos: ['Reorganización pendiente', 'Cambio de prioridades del negocio', 'Sobrecarga que limita el foco'],
    add: 'Agregar amenaza', hint: 'Factor externo a vigilar',
  },
];

const PRIORITY_COLOR = '#0F1B2D';

const FodaBoard: React.FC<Props> = ({ relId, role, counterpart, onChanged }) => {
  const [items, setItems] = useState<any[]>([]);
  const [stage, setStage] = useState<any>(null);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'intro' | 'stage'>('intro');
  const [stageIdx, setStageIdx] = useState(0); // 0-3 cuadrantes, 4 priorización
  const [saved, setSaved] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [confirmDel, setConfirmDel] = useState<any | null>(null);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [editing, setEditing] = useState(false);
  const [fTitle, setFTitle] = useState('');
  const [fEvidence, setFEvidence] = useState('');
  const [fArea, setFArea] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editCommentId, setEditCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const readOnly = role !== 'mentee';

  const firstLoad = useRef(true);
  const load = useCallback(async () => {
    const d = await planApi.getFoda(relId);
    const list = d.items || [];
    setItems(list);
    setStage(d.stage || null);
    setComments(d.comments || []);
    setPending(!!d.pending);
    // Si ya hay avance guardado, no mostramos la intro "Comenzar": retomamos el FODA
    // en el punto pendiente (primer cuadrante sin entradas, o priorización si todos tienen).
    if (firstLoad.current) {
      firstLoad.current = false;
      const editable = role === 'mentee';
      if (editable && list.length > 0) {
        // Aterriza en un cuadrante editable (el primero sin entradas, o el primero si ya están todos).
        const firstEmpty = QUADRANTS.findIndex(q => !list.some((i: any) => i.quadrant === q.key));
        setStageIdx(firstEmpty === -1 ? 0 : firstEmpty);
        setView('stage');
      }
    }
  }, [relId, role]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setFormOpen(false); }, [stageIdx]);

  const finalized = stage?.status === 'finalized';
  const itemsOf = (q: string) => items.filter(i => i.quadrant === q);

  const fmtComment = (s?: string) => {
    if (!s) return '';
    const d = new Date(String(s).replace(' ', 'T'));
    return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const sendComment = async () => {
    if (!newComment.trim() || busy) return;
    setBusy(true);
    try {
      await planApi.saveFodaComment(relId, newComment.trim());
      setNewComment('');
      await load();
      onChanged();
      showToast('Comentario enviado.', 'success');
    } catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo enviar el comentario', 'error'); }
    finally { setBusy(false); }
  };

  const delComment = async (id: number) => {
    setBusy(true);
    try { await planApi.deleteFodaComment(id); await load(); onChanged(); }
    catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo eliminar', 'error'); }
    finally { setBusy(false); }
  };

  const saveEditComment = async () => {
    if (!editCommentText.trim() || editCommentId == null) return;
    setBusy(true);
    try {
      await planApi.updateFodaComment(editCommentId, editCommentText.trim());
      setEditCommentId(null); setEditCommentText('');
      await load(); onChanged();
    } catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo actualizar', 'error'); }
    finally { setBusy(false); }
  };

  // Comentarios del mentor visibles para el mentee (donde edita/guarda su FODA).
  const menteeCommentView = (role === 'mentee' && comments.length > 0) ? (
    <div className="foda-mentor-comment foda-mentor-comment--read">
      <div className="foda-mentor-comment__label"><MessageSquare size={14} /> Comentarios de tu mentor</div>
      <div className="foda-comment-list">
        {comments.map((c) => (
          <div className="foda-comment" key={c.id}>
            <p className="foda-comment__text">{c.comment}</p>
            <span className="foda-comment__date">{fmtComment(c.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;
  const selectedCount = items.filter(i => Number(i.is_selected) === 1).length;
  const canFinalize = selectedCount >= 2 && selectedCount <= 3;

  const openForm = () => { setEditId(null); setFTitle(''); setFEvidence(''); setFArea(''); setFormOpen(true); };
  const openEdit = (it: any) => {
    setEditId(it.id); setFTitle(it.content || ''); setFEvidence(it.evidence || ''); setFArea(it.area || ''); setFormOpen(true);
    setConfirmDel(null);
  };
  const closeForm = () => { setFormOpen(false); setEditId(null); };
  const submitForm = async (quadrant: string) => {
    if (!fTitle.trim()) return;
    setBusy(true);
    try {
      if (editId) {
        await planApi.updateFodaItem(editId, { content: fTitle.trim(), evidence: fEvidence.trim() || null, area: fArea || null });
      } else {
        await planApi.createFodaItem(relId, { quadrant, content: fTitle.trim(), evidence: fEvidence.trim(), area: fArea });
      }
      setFTitle(''); setFEvidence(''); setFArea(''); setFormOpen(false); setEditId(null);
      await load(); onChanged();
    } finally { setBusy(false); }
  };
  const askDelete = (it: any) => {
    if (it.has_pdi) { setConfirmDel(it); return; }
    del(it.id);
  };
  const del = async (id: number) => { setConfirmDel(null); await planApi.deleteFodaItem(id); await load(); onChanged(); };
  const toggle = async (id: number) => {
    try { await planApi.toggleSelectFoda(id); await load(); onChanged(); }
    catch (e: any) { showToast(e?.response?.data?.message || 'Marca entre 2 y 3 líneas', 'error'); }
  };
  const finalize = async () => {
    setBusy(true);
    try {
      await planApi.finalizeFoda(relId); await load(); onChanged();
      setEditing(false);
      showToast('FODA finalizado correctamente.', 'success');
    }
    catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo finalizar', 'error'); }
    finally { setBusy(false); }
  };
  const saveDraft = async () => { setSaved(true); await load(); onChanged(); setTimeout(() => setSaved(false), 1800); };

  // ---- Vista mentor: FODA en preparación ----
  if (pending) {
    return (
      <div className="plan-card">
        <div className="plan-locked">
          <Lock size={28} className="lock-ic" />
          <h3>FODA en preparación</h3>
          <p>Tu mentee aún no ha entregado su FODA. Lo verás aquí cuando lo finalice.</p>
        </div>
      </div>
    );
  }

  // ---- Vista read-only (mentor/admin): resumen. El mentee siempre puede editar. ----
  if (readOnly) {
    return (
      <div className="foda-wiz foda-wiz--summary">
        <header className="foda-wiz__brand foda-wiz__brand--plain">FODA <span>Ejecutivo</span> de tu mentee</header>
        <div className="foda-summary-grid">
          {QUADRANTS.map(q => (
            <div className="foda-sum-quad" key={q.key} style={{ '--qc': q.color } as React.CSSProperties}>
              <div className="foda-sum-quad__head"><span className="foda-sum-quad__dot" /> {q.label}</div>
              {itemsOf(q.key).length === 0
                ? <p className="foda-sum-empty">Sin entradas.</p>
                : itemsOf(q.key).map(it => (
                  <div className="foda-sum-line" key={it.id}>
                    <span className="foda-sum-line__text">{it.content}</span>
                    {it.evidence && <span className="foda-sum-line__ev">{it.evidence}</span>}
                  </div>
                ))}
            </div>
          ))}
        </div>

        <div className="foda-mentor-comment">
          <label className="foda-mentor-comment__label"><MessageSquare size={14} /> Comentarios para tu mentee</label>
          {comments.length > 0 && (
            <div className="foda-comment-list">
              {comments.map((c) => (
                <div className="foda-comment" key={c.id}>
                  {editCommentId === c.id ? (
                    <>
                      <textarea className="foda-mentor-comment__box" value={editCommentText}
                        onChange={e => setEditCommentText(e.target.value)} />
                      <div className="foda-comment__row">
                        <span className="foda-comment__date">Editando</span>
                        <span className="foda-comment__tools">
                          <button className="foda-btn foda-btn--ink foda-btn--sm" disabled={busy || !editCommentText.trim()} onClick={saveEditComment}>
                            <Check size={13} /> Guardar
                          </button>
                          <button className="foda-comment__tool" title="Cancelar" onClick={() => { setEditCommentId(null); setEditCommentText(''); }}>Cancelar</button>
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="foda-comment__text">{c.comment}</p>
                      <div className="foda-comment__row">
                        <span className="foda-comment__date">Enviado el {fmtComment(c.created_at)}</span>
                        <span className="foda-comment__tools">
                          <button className="foda-comment__tool" title="Editar" onClick={() => { setEditCommentId(c.id); setEditCommentText(c.comment); }}><Pencil size={13} /></button>
                          <button className="foda-comment__tool foda-comment__tool--del" title="Eliminar" disabled={busy} onClick={() => delComment(c.id)}><Trash2 size={13} /></button>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <textarea
            className="foda-mentor-comment__box"
            placeholder="Escribe un comentario para tu mentee…"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
          />
          <div className="foda-mentor-comment__foot">
            <button className="foda-btn foda-btn--ink foda-btn--sm" disabled={busy || !newComment.trim()} onClick={sendComment}>
              <Send size={14} /> Enviar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Vista mentee finalizado: solo el resumen (2x2). "Editar" o clic en un cuadrante abre el modo edición. ----
  if (finalized && !editing) {
    return (
      <div className="foda-wiz foda-wiz--summary">
        <header className="foda-wiz__top">
          <div className="foda-wiz__brand">FODA <span>Ejecutivo</span></div>
          <button className="foda-btn foda-btn--outline foda-btn--sm" onClick={() => { setEditing(true); setView('stage'); setStageIdx(0); }}>
            <Pencil size={14} /> Editar
          </button>
        </header>
        <p className="foda-wiz__sub">Esta versión ya la tiene tu mentor. Si necesitas ajustarla, edítala y vuelve a guardar para enviársela de nuevo.</p>
        <div className="foda-map foda-map--summary">
          {QUADRANTS.map((qq, i) => {
            const list = itemsOf(qq.key);
            return (
              <div key={qq.key}
                className="foda-map-cell" style={{ '--qc': qq.color, '--qbg': qq.bg } as React.CSSProperties}>
                <div className="foda-map-cell__top">
                  <span className="foda-map-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="foda-map-count">{list.length} {list.length === 1 ? 'entrada' : 'entradas'}</span>
                </div>
                <div className="foda-map-label">{qq.label}</div>
                {list.length > 0
                  ? <div className="foda-map-detail">
                      {list.map(it => (
                        <div className="foda-sum-entry" key={it.id}>
                          <div className="foda-sum-entry__title">{it.content}</div>
                          {(it.evidence || it.area) && (
                            <div className="foda-sum-entry__more">
                              {it.evidence && <div className="foda-sum-entry__ev">{it.evidence}</div>}
                              {it.area && <span className="foda-sum-entry__area">{it.area}</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  : <div className="foda-map-empty">Sin entradas</div>}
              </div>
            );
          })}
        </div>
        {menteeCommentView}
      </div>
    );
  }

  // ---- Intro ----
  if (view === 'intro') {
    const hasProgress = items.length > 0;
    const firstEmpty = QUADRANTS.findIndex(q => itemsOf(q.key).length === 0);
    const resumeIdx = firstEmpty === -1 ? 0 : firstEmpty;
    return (
      <div className="foda-wiz">
        {showExample && <FodaExampleModal onClose={() => setShowExample(false)} />}
        <div className="foda-intro-card">
          <header className="foda-wiz__brand">FODA <span>Ejecutivo</span>
            <button className="fw-example-btn" onClick={() => setShowExample(true)}><Info size={15} /> Ver ejemplo de FODA</button>
          </header>
          <p className="foda-intro-desc">
            Cada etapa aborda un cuadrante específico y trae preguntas guía para enmarcar la reflexión. El contenido se preserva entre sesiones; puedes pausar y retomarlo cuando convenga.
          </p>
          <div className="foda-intro-list">
            {QUADRANTS.map((q, i) => {
              const n = itemsOf(q.key).length;
              return (
                <div className="foda-intro-item" key={q.key} style={{ '--qc': q.color } as React.CSSProperties}>
                  <span className="foda-intro-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="foda-intro-label">{q.label}</span>
                  <span className="foda-intro-quaddesc">{q.desc}</span>
                  {hasProgress && (
                    <span className={`foda-intro-count ${n > 0 ? 'has' : ''}`}>{n > 0 ? `${n} entrada${n > 1 ? 's' : ''}` : 'Sin entradas'}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="foda-intro-foot">
            <span className="foda-intro-time"><Clock size={15} /> Tiempo estimado: 15-20 minutos</span>
            <button className="foda-btn foda-btn--ink" onClick={() => { setStageIdx(hasProgress ? resumeIdx : 0); setView('stage'); }}>
              {hasProgress ? 'Continuar' : 'Comenzar'} <ArrowRight size={16} />
            </button>
          </div>
        </div>
        {menteeCommentView}
      </div>
    );
  }

  const q = QUADRANTS[Math.min(stageIdx, 3)];
  const isLast = stageIdx >= 3;
  const brechaCount = items.filter(i => i.quadrant !== 'fortaleza').length;
  const canFinalizeFoda = brechaCount >= 1;

  return (
    <div className="foda-wiz" style={{ '--qc': q.color, '--qbg': q.bg } as React.CSSProperties}>
      {showExample && <FodaExampleModal onClose={() => setShowExample(false)} />}

      {confirmFinalize && (
        <div className="fw-modal-overlay" onClick={() => setConfirmFinalize(false)}>
          <div className="fw-modal" onClick={e => e.stopPropagation()}>
            <div className="fw-modal__icon"><ShieldCheck size={20} /></div>
            <h3 className="fw-modal__title">Esta versión se enviará a tu mentor</h3>
            <p className="fw-modal__text">
              Al finalizar, esta versión de tu FODA se enviará a la cuenta de tu mentor. ¿Estás listo para lo que verá?
              Si aún lo estás trabajando, guarda un borrador y retómalo cuando quieras.
            </p>
            <div className="fw-modal__actions">
              <button className="foda-btn foda-btn--ink" disabled={busy} onClick={() => { setConfirmFinalize(false); finalize(); }}>
                <Send size={15} /> Finalizar y enviar
              </button>
              <button className="foda-btn foda-btn--outline" onClick={() => { setConfirmFinalize(false); saveDraft(); }}>
                <Check size={15} /> Guardar borrador
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fw-modal-overlay" onClick={() => setConfirmDel(null)}>
          <div className="fw-modal" onClick={e => e.stopPropagation()}>
            <div className="fw-modal__icon"><AlertTriangle size={20} /></div>
            <h3 className="fw-modal__title">Este ítem alimenta tu PDI</h3>
            <p className="fw-modal__text">
              “{confirmDel.content}” tiene una <strong>competencia del PDI asociada</strong>. Si lo borras,
              también se eliminarán esa competencia y todas sus acciones. ¿Prefieres editarlo?
            </p>
            <div className="fw-modal__actions">
              <button className="foda-btn foda-btn--ink" onClick={() => openEdit(confirmDel)}><Pencil size={15} /> Editar en su lugar</button>
              <button className="fw-modal__danger" onClick={() => del(confirmDel.id)}><Trash2 size={15} /> Borrar ambos</button>
              <button className="fw-form-cancel" onClick={() => setConfirmDel(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      <header className="foda-wiz__top">
        <div>
          <div className="foda-wiz__brand">FODA <span>Ejecutivo</span>
            <button className="fw-example-btn" onClick={() => setShowExample(true)}><Info size={15} /> Ver ejemplo de FODA</button>
          </div>
        </div>
      </header>

      <div className="foda-topbar">
        <button className="foda-btn foda-btn--outline foda-btn--sm" disabled={busy} onClick={saveDraft}>
          <Check size={14} /> {saved ? 'Guardado' : 'Guardar borrador'}
        </button>
      </div>

      {/* Navegación libre entre cuadrantes (editable en todo momento) */}
      <div className="foda-tabs">
        {QUADRANTS.map((qq, i) => (
          <button key={qq.key} className={`foda-tab ${stageIdx === i ? 'on' : ''}`}
            style={{ '--qc': qq.color } as React.CSSProperties} onClick={() => setStageIdx(i)}>
            {qq.label}
            {itemsOf(qq.key).length > 0 && <span className="foda-tab-n">{itemsOf(qq.key).length}</span>}
          </button>
        ))}
      </div>

      {(
        <>
          <h2 className="foda-stage-title">{q.label}</h2>
          <p className="foda-stage-desc">{q.desc}</p>

          {/* Mapa 2x2 — resumen con las entradas de cada cuadrante (clic para editar) */}
          <div className="foda-map">
            {QUADRANTS.map((qq, i) => {
              const active = i === stageIdx;
              const list = itemsOf(qq.key);
              return (
                <button key={qq.key} type="button" onClick={() => setStageIdx(i)}
                  className={`foda-map-cell ${active ? 'is-active' : ''}`}
                  style={{ '--qc': qq.color, '--qbg': qq.bg } as React.CSSProperties}>
                  <div className="foda-map-cell__top">
                    <span className="foda-map-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="foda-map-count">{list.length} {list.length === 1 ? 'entrada' : 'entradas'}</span>
                  </div>
                  <div className="foda-map-label">{qq.label}</div>
                  {list.length > 0
                    ? <ul className="foda-map-items">{list.map(it => <li key={it.id}>{it.content}</li>)}</ul>
                    : <div className="foda-map-empty">Sin entradas aún</div>}
                </button>
              );
            })}
          </div>

          {/* Preguntas guía */}
          <div className="foda-guide">
            <div className="foda-eyebrow">Preguntas guía</div>
            {q.questions.map((qq, i) => <div className="foda-guide-q" key={i}>— {qq}</div>)}
            <div className="foda-guide-sep" />
            <p className="foda-guide-ex"><strong>Ejemplos</strong> · {q.ejemplos.join(' · ')}</p>
          </div>

          {/* Detalle */}
          <div className="foda-detail">
            <div className="foda-detail__head">
              <span className="foda-eyebrow">Detalle — {q.label}</span>
              <span className="foda-detail__count">{itemsOf(q.key).length === 0 ? 'Aún sin entradas' : `${itemsOf(q.key).length} entrada(s)`}</span>
            </div>
            {itemsOf(q.key).map(it => (
              <div className="fw-entry" key={it.id}>
                <div className="fw-entry__title">{it.content}</div>
                {it.evidence && <div className="fw-entry__ev">{it.evidence}</div>}
                <div className="fw-entry__foot">
                  {it.area && <span className="fw-area-chip">{it.area}</span>}
                  {it.has_pdi && <span className="fw-pdi-chip"><Check size={11} /> En tu PDI</span>}
                  <span className="fw-entry__actions">
                    <button className="fw-entry__edit" onClick={() => openEdit(it)} title="Editar"><Pencil size={14} /></button>
                    <button className="fw-entry__del" onClick={() => askDelete(it)} title="Eliminar"><Trash2 size={14} /></button>
                  </span>
                </div>
              </div>
            ))}

            {formOpen ? (
              <div className="fw-form">
                <div className="fw-form-field">
                  <span className="fw-form-label">Título · nombra la {q.key} en una frase</span>
                  <input className="fw-form-input" autoFocus value={fTitle} placeholder={`Ej. ${q.ejemplos[0]}`}
                    onChange={e => setFTitle(e.target.value)} />
                </div>
                <div className="fw-form-field">
                  <span className="fw-form-label">Evidencia · un caso o dato concreto</span>
                  <textarea className="fw-form-textarea" rows={3} value={fEvidence}
                    placeholder="¿Qué situación, qué hiciste, qué resultado? La generalidad no le sirve a tu mentor."
                    onChange={e => setFEvidence(e.target.value)} />
                </div>
                <div className="fw-form-field">
                  <span className="fw-form-label">Área</span>
                  <div className="fw-area-tags">
                    {AREAS.map(a => (
                      <button key={a} type="button" className={`fw-area-tag ${fArea === a ? 'on' : ''}`}
                        onClick={() => setFArea(fArea === a ? '' : a)}>{a}</button>
                    ))}
                  </div>
                </div>
                <div className="fw-form-actions">
                  <button className="foda-btn foda-btn--ink" disabled={busy || !fTitle.trim()} onClick={() => submitForm(q.key)}>{editId ? 'Actualizar' : 'Guardar'}</button>
                  <button className="fw-form-cancel" onClick={closeForm}>Cancelar</button>
                </div>
              </div>
            ) : (
              <button className="fw-add-trigger" onClick={openForm}>
                <span className="fw-add-trigger__ic"><Plus size={18} /></span>
                <span className="fw-add-trigger__txt"><strong>{q.add}</strong><span>{q.hint}</span></span>
              </button>
            )}
          </div>

          {menteeCommentView}

          <div className="foda-wiz__nav">
            <button className="foda-btn foda-btn--ghost" onClick={() => stageIdx === 0 ? setView('intro') : setStageIdx(stageIdx - 1)}>
              <ArrowLeft size={15} /> Anterior
            </button>
            <button className="foda-btn foda-btn--outline" onClick={saveDraft}>
              <Check size={15} /> {saved ? 'Guardado' : 'Guardar borrador'}
            </button>
            {isLast ? (
              <button className="foda-btn foda-btn--ink" disabled={!canFinalizeFoda || busy}
                onClick={() => (role === 'mentee' ? setConfirmFinalize(true) : finalize())}
                title={!canFinalizeFoda ? 'Agrega al menos una debilidad, oportunidad o amenaza' : ''}>
                <Check size={16} /> Finalizar FODA
              </button>
            ) : (
              <button className="foda-btn foda-btn--ink" onClick={() => setStageIdx(stageIdx + 1)}>
                Siguiente: {QUADRANTS[stageIdx + 1].label} <ArrowRight size={15} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FodaBoard;
